import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Divider, Image, Input, Row, Space, Spin, Tag, Typography, Upload, message } from 'antd';
import { AimOutlined, BuildOutlined, EditOutlined } from '@ant-design/icons';
import Map, { MapRef } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import dayjs from 'dayjs';
import API_BASE_URL, { ENDPOINTS } from '../../helpers/api/apiConfig';
import { useAuth } from '../../context/AuthContext';
import 'mapbox-gl/dist/mapbox-gl.css';

const { Text } = Typography;

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

interface SearchResult {
    id: string;
    place_name: string;
    center: [number, number];
}

interface CompanyManagementTabProps {
    isAdmin: boolean;
    isMaster: boolean;
    companyName: string;
    companyId?: number;
    buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
    onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

const CompanyManagementTab: React.FC<CompanyManagementTabProps> = ({
    isAdmin,
    isMaster,
    companyName,
    companyId,
    buildAuthHeaders,
    onAuthMismatch,
}) => {
    const [companyInfo, setCompanyInfo] = useState<any>(null);
    const [companyInfoLoading, setCompanyInfoLoading] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);

    // Root location (Point area) state
    const [rootLocationName, setRootLocationName] = useState('');
    const [rootPin, setRootPin] = useState<{ lat: number; lng: number } | null>(null);
    const [rootSaving, setRootSaving] = useState(false);
    const [rootMapLoaded, setRootMapLoaded] = useState(false);
    // true when user is actively editing (or no root exists yet), false = read-only view
    const [isRootEditing, setIsRootEditing] = useState(false);

    // Search bar for the root location map
    const [rootSearchQuery, setRootSearchQuery] = useState('');
    const [rootSearchResults, setRootSearchResults] = useState<SearchResult[]>([]);
    const [rootSearching, setRootSearching] = useState(false);
    const rootSearchTimeout = useRef<any>(null);

    const [rootMapViewState, setRootMapViewState] = useState({ longitude: -0.1278, latitude: 51.5074, zoom: 5 });
    const rootMapRef = useRef<MapRef | null>(null);
    const rootMarkerRef = useRef<mapboxgl.Marker | null>(null);

    const { currentUser, loginUser } = useAuth();
    const allowedLogoTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];

    const fetchCompanyInfo = async () => {
        if (!companyId) return;
        setCompanyInfoLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
                headers: { ...buildAuthHeaders() },
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setCompanyInfo(data || null);
            } else {
                const errorData = await response.json();
                if (await onAuthMismatch(response, errorData)) return;
                message.error(errorData?.message || 'Failed to load company details');
            }
        } catch (error) {
            console.error('Failed to load company details', error);
            message.error('Failed to load company details');
        } finally {
            setCompanyInfoLoading(false);
        }
    };

    // Load root location from existing rootAreaId when company data arrives
    useEffect(() => {
        if (!companyInfo?.rootAreaId) return;
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/areas/${companyInfo.rootAreaId}`, {
                    headers: { ...buildAuthHeaders() },
                    credentials: 'include',
                });
                if (!res.ok) return;
                const data = await res.json();
                const area = data?.data || data;
                if (!area) return;
                setRootLocationName(area.name || '');
                // GeoJSON Point: coordinates = [lng, lat]
                if (area.geom?.type === 'Point' && Array.isArray(area.geom.coordinates)) {
                    const [lng, lat] = area.geom.coordinates;
                    setRootPin({ lat, lng });
                    setRootMapViewState({ longitude: lng, latitude: lat, zoom: 13 });
                }
            } catch { /* non-critical */ }
        })();
    }, [companyInfo?.rootAreaId]);

    useEffect(() => {
        if (!isAdmin && !isMaster) return;
        fetchCompanyInfo();
    }, [companyId, isAdmin, isMaster]);

    // Place/move marker — draggable only when editing; fly to zoom 20 in read-only mode
    useEffect(() => {
        const map = rootMapRef.current?.getMap?.();
        if (!map || !rootMapLoaded) return;
        if (rootMarkerRef.current) {
            rootMarkerRef.current.remove();
            rootMarkerRef.current = null;
        }
        if (!rootPin) return;
        const el = document.createElement('div');
        el.style.cssText = 'width:28px;height:28px;background:#722ed1;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.35);cursor:pointer;';
        rootMarkerRef.current = new mapboxgl.Marker({ element: el, draggable: isRootEditing })
            .setLngLat([rootPin.lng, rootPin.lat])
            .addTo(map);
        if (isRootEditing) {
            rootMarkerRef.current.on('dragend', () => {
                const lngLat = rootMarkerRef.current!.getLngLat();
                setRootPin({ lat: lngLat.lat, lng: lngLat.lng });
            });
        } else {
            // Read-only: zoom into the pin at zoom 20
            map.flyTo({ center: [rootPin.lng, rootPin.lat], zoom: 20, duration: 800 });
        }
    }, [rootPin, rootMapLoaded, isRootEditing]);

    const handleRootMapClick = useCallback((e: any) => {
        const { lngLat } = e;
        setRootPin({ lat: lngLat.lat, lng: lngLat.lng });
    }, []);

    const handleRootSearch = async (query: string) => {
        if (!query.trim()) { setRootSearchResults([]); return; }
        setRootSearching(true);
        try {
            const res = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5`,
            );
            const data = await res.json();
            setRootSearchResults((data?.features || []).map((f: any) => ({
                id: f.id,
                place_name: f.place_name,
                center: f.center,
            })));
        } catch { /* ignore */ } finally {
            setRootSearching(false);
        }
    };

    const selectRootSearchResult = (r: SearchResult) => {
        const [lng, lat] = r.center;
        setRootPin({ lat, lng });
        setRootMapViewState({ longitude: lng, latitude: lat, zoom: 14 });
        rootMapRef.current?.flyTo({ center: [lng, lat], zoom: 14 });
        setRootSearchQuery(r.place_name);
        setRootSearchResults([]);
    };

    const handleSaveRootLocation = async () => {
        if (!companyId) return;
        if (!rootLocationName.trim()) { message.warning('Enter a name for the root location'); return; }
        if (!rootPin) { message.warning('Click the map to place a pin first'); return; }
        setRootSaving(true);
        try {
            const res = await fetch(ENDPOINTS.COMPANY_ROOT_AREA(companyId), {
                method: 'PATCH',
                headers: buildAuthHeaders(true),
                credentials: 'include',
                body: JSON.stringify({ name: rootLocationName.trim(), lat: rootPin.lat, lng: rootPin.lng }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to save root location');
            }
            message.success('Root location saved');
            await fetchCompanyInfo();
            setIsRootEditing(false); // return to read-only mode after save
        } catch (e: any) {
            message.error(e?.message || 'Failed to save root location');
        } finally {
            setRootSaving(false);
        }
    };

    const handleClearRootLocation = async () => {
        if (!companyId) return;
        setRootSaving(true);
        try {
            const res = await fetch(ENDPOINTS.COMPANY_ROOT_AREA(companyId), {
                method: 'PATCH',
                headers: buildAuthHeaders(true),
                credentials: 'include',
                body: JSON.stringify({ rootAreaId: null }),
            });
            if (!res.ok) throw new Error('Failed to clear root location');
            setRootPin(null);
            setRootLocationName('');
            message.success('Root location cleared');
        } catch (e: any) {
            message.error(e?.message || 'Failed to clear root location');
        } finally {
            setRootSaving(false);
        }
    };

    if (!isAdmin && !isMaster) {
        return (
            <Alert
                message="Access Restricted"
                description="Only administrators can access company management features."
                type="warning"
                showIcon
            />
        );
    }

    return (
        <Card
            className="configuration-company-card"
            title={<Space><BuildOutlined />Company Management</Space>}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>

                {/* ── Company Information ─────────────────────────────────── */}
                <div className="configuration-company-info-section">
                    <Text strong>Company Information</Text>
                    <div style={{ marginTop: 12 }}>
                        <Spin spinning={companyInfoLoading}>
                            <Row gutter={[16, 16]} align="middle">
                                <Col xs={24} md={6}>
                                    <Space direction="vertical" size="small" align="center" style={{ width: '100%' }}>
                                        {companyInfo?.logoUrl ? (
                                            <Image
                                                src={companyInfo.logoUrl}
                                                width={120}
                                                height={120}
                                                style={{ objectFit: 'contain', borderRadius: 8 }}
                                                preview={false}
                                            />
                                        ) : (
                                            <div style={{ width: 120, height: 120, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c' }}>
                                                No logo
                                            </div>
                                        )}
                                        <Upload
                                            showUploadList={false}
                                            accept={allowedLogoTypes.join(',')}
                                            beforeUpload={(file) => {
                                                if (!allowedLogoTypes.includes(file.type)) {
                                                    message.error('Only PNG, JPG, WebP, and SVG images are allowed.');
                                                    return Upload.LIST_IGNORE;
                                                }
                                                if (file.size > 5 * 1024 * 1024) {
                                                    message.error('Logo file must be smaller than 5MB.');
                                                    return Upload.LIST_IGNORE;
                                                }
                                                return true;
                                            }}
                                            customRequest={async ({ file, onSuccess, onError }) => {
                                                if (!companyId) return;
                                                setLogoUploading(true);
                                                try {
                                                    const formData = new FormData();
                                                    formData.append('logo', file as File);
                                                    const uploadResponse = await fetch(`${API_BASE_URL}/upload-company-logo`, {
                                                        method: 'POST',
                                                        body: formData,
                                                        headers: { ...buildAuthHeaders(false) },
                                                        credentials: 'include',
                                                    });
                                                    if (!uploadResponse.ok) {
                                                        let errorMessage = 'Logo upload failed';
                                                        try {
                                                            const errorData = await uploadResponse.json();
                                                            errorMessage = errorData?.message || errorMessage;
                                                            if (await onAuthMismatch(uploadResponse, errorData)) return;
                                                        } catch { /* ignore */ }
                                                        throw new Error(errorMessage);
                                                    }
                                                    const uploadData = await uploadResponse.json();
                                                    const logoUrl = uploadData?.logoUrl;
                                                    const logoReference = uploadData?.fileName || logoUrl;
                                                    if (!logoReference) throw new Error('Logo upload returned no URL');

                                                    const updateResponse = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
                                                        method: 'PATCH',
                                                        headers: { ...buildAuthHeaders(true) },
                                                        credentials: 'include',
                                                        body: JSON.stringify({ logoUrl: logoReference }),
                                                    });
                                                    if (!updateResponse.ok) {
                                                        const parsedBody = await updateResponse.json().catch(() => null);
                                                        if (await onAuthMismatch(updateResponse, parsedBody)) return;
                                                        throw new Error(parsedBody?.message || 'Failed to update company logo');
                                                    }

                                                    setCompanyInfo((prev: any) => ({ ...(prev || {}), logoUrl }));
                                                    if (currentUser) {
                                                        try {
                                                            const companiesResponse = await fetch(`${API_BASE_URL}/companies`, {
                                                                headers: { ...buildAuthHeaders() },
                                                                credentials: 'include',
                                                            });
                                                            if (companiesResponse.ok) {
                                                                const companies = await companiesResponse.json();
                                                                loginUser({
                                                                    ...currentUser,
                                                                    associatedCompanies: companies,
                                                                    companyMemberships: currentUser.companyMemberships?.map((m) =>
                                                                        m.company?.id === companyId
                                                                            ? { ...m, company: { ...m.company, logoUrl } }
                                                                            : m
                                                                    ),
                                                                });
                                                            }
                                                        } catch { /* non-critical */ }
                                                    }
                                                    message.success('Company logo updated');
                                                    if (onSuccess) onSuccess({}, new XMLHttpRequest());
                                                } catch (error: any) {
                                                    message.error(error?.message || 'Failed to update company logo');
                                                    if (onError) onError(error);
                                                } finally {
                                                    setLogoUploading(false);
                                                }
                                            }}
                                        >
                                            <Button className="configuration-company-logo-button" loading={logoUploading} disabled={!companyId}>
                                                Change logo
                                            </Button>
                                        </Upload>
                                    </Space>
                                </Col>
                                <Col xs={24} md={18}>
                                    <Descriptions size="small" column={1} className="configuration-company-descriptions">
                                        <Descriptions.Item label="Name">{companyInfo?.name || companyName}</Descriptions.Item>
                                        <Descriptions.Item label="Address">{companyInfo?.address || '—'}</Descriptions.Item>
                                        <Descriptions.Item label="Company ID">{companyId ?? '—'}</Descriptions.Item>
                                        <Descriptions.Item label="Updated">
                                            {companyInfo?.updatedAt ? dayjs(companyInfo.updatedAt).format('YYYY-MM-DD') : '—'}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Col>
                            </Row>
                        </Spin>
                    </div>
                </div>

                {/* ── Root Location ────────────────────────────────────────── */}
                <Divider style={{ margin: '8px 0' }} />
                <div>
                    <Space align="center" style={{ marginBottom: 4 }}>
                        <AimOutlined style={{ color: '#722ed1' }} />
                        <Text strong>Root Location</Text>
                        {rootPin && (
                            <Tag color="purple">{rootPin.lat.toFixed(5)}, {rootPin.lng.toFixed(5)}</Tag>
                        )}
                        {/* Edit button — only visible in read-only mode (root exists) */}
                        {rootPin && !isRootEditing && (
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => setIsRootEditing(true)}
                                style={{ marginLeft: 8 }}
                            >
                                Edit
                            </Button>
                        )}
                    </Space>

                    {/* Description — only shown when editing / no root yet */}
                    {(!rootPin || isRootEditing) && (
                        <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 10 }}>
                            Set your company's primary GPS location (a single point). Used as the default map centre when creating new areas.
                            Click the map to drop a pin, or drag the pin to adjust.
                        </div>
                    )}

                    {/* Name: read-only text OR editable input */}
                    {rootPin && !isRootEditing ? (
                        <div style={{ marginBottom: 8 }}>
                            <Text><AimOutlined style={{ color: '#722ed1', marginRight: 6 }} />{rootLocationName}</Text>
                        </div>
                    ) : (
                        <Row gutter={8} style={{ marginBottom: 8 }}>
                            <Col flex="1">
                                <Input
                                    placeholder="Location name (e.g. Wembley Stadium)"
                                    value={rootLocationName}
                                    onChange={e => setRootLocationName(e.target.value)}
                                    prefix={<AimOutlined style={{ color: '#722ed1' }} />}
                                />
                            </Col>
                        </Row>
                    )}

                    {/* Search bar — only shown when editing */}
                    {isRootEditing && (
                        <div style={{ position: 'relative', marginBottom: 6 }}>
                            <Input
                                placeholder="Search for a place…"
                                value={rootSearchQuery}
                                onChange={e => {
                                    setRootSearchQuery(e.target.value);
                                    clearTimeout(rootSearchTimeout.current);
                                    rootSearchTimeout.current = setTimeout(() => handleRootSearch(e.target.value), 300);
                                }}
                            />
                            {rootSearching && <div style={{ position: 'absolute', right: 8, top: 8 }}><Spin size="small" /></div>}
                            {rootSearchResults.length > 0 && (
                                <div style={{ position: 'absolute', zIndex: 10, width: '100%', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                    {rootSearchResults.map(r => (
                                        <div
                                            key={r.id}
                                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}
                                            onClick={() => selectRootSearchResult(r)}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                                            onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                        >
                                            {r.place_name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Map — non-interactive in read-only mode, interactive when editing */}
                    <div style={{
                        height: 280,
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid #e8e8e8',
                        marginBottom: 10,
                        cursor: isRootEditing ? 'crosshair' : 'default',
                    }}>
                        <Map
                            ref={rootMapRef}
                            mapboxAccessToken={MAPBOX_TOKEN}
                            initialViewState={rootMapViewState}
                            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                            style={{ width: '100%', height: '100%' }}
                            onLoad={() => setRootMapLoaded(true)}
                            onClick={isRootEditing ? handleRootMapClick : undefined}
                            scrollZoom={isRootEditing}
                            dragPan={isRootEditing}
                            dragRotate={false}
                            doubleClickZoom={isRootEditing}
                            touchZoomRotate={isRootEditing}
                        />
                    </div>

                    {/* Action buttons — Save/Cancel when editing, Clear when read-only */}
                    {isRootEditing ? (
                        <Row gutter={8}>
                            <Col>
                                <Button
                                    type="primary"
                                    loading={rootSaving}
                                    disabled={!rootPin || !rootLocationName.trim()}
                                    onClick={handleSaveRootLocation}
                                    style={{ background: '#722ed1', borderColor: '#722ed1' }}
                                >
                                    Save root location
                                </Button>
                            </Col>
                            {companyInfo?.rootAreaId && (
                                <Col>
                                    <Button onClick={() => setIsRootEditing(false)}>
                                        Cancel
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    ) : (
                        companyInfo?.rootAreaId && (
                            <Button danger size="small" loading={rootSaving} onClick={handleClearRootLocation}>
                                Clear root location
                            </Button>
                        )
                    )}
                </div>

            </Space>
        </Card>
    );
};

export default CompanyManagementTab;
